import api from './api';
import { Competitor, CompetitorAnalysis, ApiResponse, PaginatedResponse } from '../types';

interface CompetitorFilters {
  page?: number;
  limit?: number;
  search?: string;
  industry?: string;
  is_active?: boolean;
}

export const competitorService = {
  async getCompetitors(filters?: CompetitorFilters): Promise<PaginatedResponse<Competitor>> {
    const response = await api.get('/competitors', { params: filters });
    return response.data;
  },

  async getCompetitor(id: string): Promise<ApiResponse<Competitor>> {
    const response = await api.get(`/competitors/${id}`);
    return response.data;
  },

  async createCompetitor(data: Partial<Competitor>): Promise<ApiResponse<Competitor>> {
    const response = await api.post('/competitors', data);
    return response.data;
  },

  async updateCompetitor(id: string, data: Partial<Competitor>): Promise<ApiResponse<Competitor>> {
    const response = await api.put(`/competitors/${id}`, data);
    return response.data;
  },

  async deleteCompetitor(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/competitors/${id}`);
    return response.data;
  },

  async analyzeCompetitor(id: string, type?: string): Promise<ApiResponse<CompetitorAnalysis>> {
    const response = await api.post(`/competitors/${id}/analyze`, {}, {
      params: { type }
    });
    return response.data;
  },

  async updateAnalysisAdsData(id: string, data: {
    ads_data?: any;
    paid_keywords_list?: any[];
    paid_traffic_value?: number;
    has_active_ads?: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await api.patch(`/competitors/${id}/analysis/ads`, data);
    return response.data;
  },

  async loadMoreGoogleAds(id: string, batchSize: number = 20): Promise<ApiResponse<{ newAds: any[]; totalAdsNow: number; hasMore: boolean; nextCursor?: string }>> {
    const response = await api.post(`/competitors/${id}/ads/load-more-google`, { batchSize });
    return response.data;
  },

  async loadMoreMetaAds(id: string, batchSize: number = 20): Promise<ApiResponse<{ newAds: any[]; totalAdsNow: number; hasMore: boolean }>> {
    const response = await api.post(`/competitors/${id}/ads/load-more-meta`, { batchSize });
    return response.data;
  },

  async compareCompetitors(ids: string[]): Promise<ApiResponse<any>> {
    const response = await api.get('/competitors/compare', {
      params: { ids: ids.join(',') }
    });
    return response.data;
  },

  async analyze(id: string, type?: string, useAsync?: boolean): Promise<ApiResponse<any> & { async?: boolean; jobId?: string }> {
    const response = await api.post(`/competitors/${id}/analyze`, {}, {
      params: { type, async: useAsync ?? true }
    });
    return response.data;
  },

  async getKeywords(id: string, params?: { page?: number; limit?: number }): Promise<ApiResponse<any[]>> {
    const response = await api.get(`/competitors/${id}/keywords`, { params });
    return response.data;
  },

  async getBacklinks(id: string, params?: { page?: number; limit?: number; type?: string }): Promise<ApiResponse<any[]>> {
    const response = await api.get(`/competitors/${id}/backlinks`, { params });
    return response.data;
  },

  async getRankings(id: string, params?: { days?: number }): Promise<ApiResponse<any[]>> {
    const response = await api.get(`/competitors/${id}/rankings`, { params });
    return response.data;
  },

  async getTraffic(id: string): Promise<ApiResponse<any>> {
    const response = await api.get(`/competitors/${id}/traffic`);
    return response.data;
  },

  async startAudit(id: string): Promise<ApiResponse<any>> {
    const response = await api.post(`/competitors/${id}/audit`);
    return response.data;
  },

  // Site Explorer - analyze any domain
  async exploreSite(domain: string): Promise<ApiResponse<any>> {
    const response = await api.get('/competitors/explore/site', { params: { domain } });
    return response.data;
  },

  // Backlinks Explorer - analyze backlinks of any domain
  async exploreBacklinks(domain: string): Promise<ApiResponse<any>> {
    const response = await api.get('/competitors/explore/backlinks', { params: { domain } });
    return response.data;
  },

  // Get exploration history with pagination
  async getExplorationHistory(params?: { page?: number; limit?: number; search?: string }): Promise<ApiResponse<any>> {
    const response = await api.get('/competitors/explore/history', { params });
    return response.data;
  },

  // Get single exploration by ID
  async getExplorationById(explorationId: string): Promise<ApiResponse<any>> {
    const response = await api.get(`/competitors/explore/history/${explorationId}`);
    return response.data;
  },

  // Delete exploration
  async deleteExploration(explorationId: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/competitors/explore/history/${explorationId}`);
    return response.data;
  },

  // Site Audit - run full SEO audit on any URL
  async runSiteAudit(url: string, save: boolean = true, useAsync: boolean = true): Promise<ApiResponse<any> & { async?: boolean; jobId?: string }> {
    const response = await api.post('/competitors/audit/site', { url, save, async: useAsync });
    return response.data;
  },

  // Get audit history with pagination
  async getAuditHistory(params?: { page?: number; limit?: number; search?: string }): Promise<ApiResponse<any>> {
    const response = await api.get('/competitors/audit/history', { params });
    return response.data;
  },

  // Get single audit by ID
  async getAuditById(auditId: string): Promise<ApiResponse<any>> {
    const response = await api.get(`/competitors/audit/${auditId}`);
    return response.data;
  },

  // Delete audit
  async deleteAudit(auditId: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/competitors/audit/${auditId}`);
    return response.data;
  },

  // Get latest audit by URL (for loading saved audit on competitor page)
  async getLatestAuditByUrl(url: string): Promise<ApiResponse<any>> {
    const response = await api.get('/competitors/audit/latest', { params: { url } });
    return response.data;
  },

  // ============ Google Search Console ============

  // Check Search Console connection status
  async getSearchConsoleStatus(): Promise<ApiResponse<any>> {
    const response = await api.get('/competitors/search-console/status');
    return response.data;
  },

  // Get verified sites from Search Console
  async getSearchConsoleSites(): Promise<ApiResponse<any[]>> {
    const response = await api.get('/competitors/search-console/sites');
    return response.data;
  },

  // Get keywords from Search Console
  async getSearchConsoleKeywords(siteUrl: string, params?: { startDate?: string; endDate?: string; limit?: number }): Promise<ApiResponse<any>> {
    const response = await api.get('/competitors/search-console/keywords', {
      params: { siteUrl, ...params }
    });
    return response.data;
  },

  // Get top pages from Search Console
  async getSearchConsolePages(siteUrl: string, params?: { startDate?: string; endDate?: string; limit?: number }): Promise<ApiResponse<any>> {
    const response = await api.get('/competitors/search-console/pages', {
      params: { siteUrl, ...params }
    });
    return response.data;
  },

  // Get full analytics from Search Console
  async getSearchConsoleAnalytics(siteUrl: string, params?: { startDate?: string; endDate?: string; dimensions?: string }): Promise<ApiResponse<any>> {
    const response = await api.get('/competitors/search-console/analytics', {
      params: { siteUrl, ...params }
    });
    return response.data;
  },

  // ============ Ads Intelligence (Google Ads Transparency) ============

  // Get available regions for ads exploration
  async getAdsRegions(): Promise<ApiResponse<{ code: string; name: string; flag: string }[]>> {
    const response = await api.get('/competitors/explore/ads/regions');
    return response.data;
  },

  // Explore competitor ads from Google Ads Transparency Center
  async exploreCompetitorAds(domain: string, params?: { region?: string; maxAds?: number; async?: boolean }): Promise<ApiResponse<any> & { async?: boolean; jobId?: string }> {
    const response = await api.get('/competitors/explore/ads', {
      params: { domain, async: params?.async ?? true, ...params }
    });
    return response.data;
  },

  // Explore competitor Meta (Facebook/Instagram) ads
  async exploreMetaAds(domain: string, params?: { countryCode?: string; maxAds?: number; async?: boolean }): Promise<ApiResponse<any> & { async?: boolean; jobId?: string }> {
    const response = await api.get('/competitors/explore/meta-ads', {
      params: { domain, async: params?.async ?? true, ...params }
    });
    return response.data;
  }
};
